import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { indexOf } from 'ramda';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  styled
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import IntegrationModal from '../CreateIntegration/IntegrationModal';
import { ModalContext } from '../CreateIntegration/ModalContext';
import {
  validateSeedTalentIntegration,
  trimSeedTalentValues
} from '../CreateIntegration/seedTalentFormShared';
import { fixedEventTypeFor } from '../CreateIntegration/seedTalentIntegrationTypes';
import {
  deleteIntegration,
  createIntegration
} from '../../../../_services/ApiService';
import ConfirmButton from 'components/ConfirmButton';
import { useSearchParam } from 'helpers/useSearchParam';
import { useIntegrationsContext } from '../IntegrationsContext';

const ActionsRow = styled('div')(({ theme }) => ({
  height: '42px',
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  columnGap: theme.spacing(1)
}));

const Spacer = styled('span')({
  flexGrow: 1
});

const FilterPanel = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const FilterField = styled(Box)({
  minWidth: 160,
  flex: '1 1 160px',
  maxWidth: 240
});

const WideFilterField = styled(Box)({
  minWidth: 180,
  flex: '1 1 200px',
  maxWidth: 280
});

const clearableInputProps = (value, onClear) => ({
  endAdornment: value ? (
    <InputAdornment position="end">
      <IconButton
        aria-label="Clear filter"
        edge="end"
        onClick={onClear}
        size="small"
      >
        <ClearIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ) : null
});

const SearchableSelect = ({
  label,
  options,
  value,
  onChange,
  placeholder
}) => {
  const selected =
    options.find((option) => option.value === value) || null;

  return (
    <FilterField>
      <Autocomplete
        clearOnEscape
        getOptionLabel={(option) => option.label || ''}
        isOptionEqualToValue={(option, selectedValue) =>
          option.value === selectedValue.value
        }
        onChange={(_event, next) => onChange(next?.value || '')}
        options={options}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder || 'Search…'}
            size="small"
          />
        )}
        size="small"
        value={selected}
      />
    </FilterField>
  );
};

const ClearableTextFilter = ({
  label,
  value,
  onChange,
  placeholder
}) => (
  <WideFilterField>
    <TextField
      fullWidth
      InputProps={clearableInputProps(value, () => onChange(''))}
      label={label}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      size="small"
      value={value || ''}
    />
  </WideFilterField>
);

const emptyValues = {
  _id: '',
  companyName: '',
  integrationId: '',
  eventType: '',
  config: '{ }',
  source: '{ }',
  createdDate: '',
  updatedDate: ''
};

const IntegrationsToolbar = (props) => {
  const {
    className,
    integrations,
    setIntegrations,
    selectedIntegrations,
    setSelectedIntegrations,
    ...rest
  } = props;

  const {
    companies,
    eventTypes,
    onCompanyChange,
    onEventTypeChange,
    onIntegrationIdChange,
    onClearAll
  } = useIntegrationsContext();

  const [company] = useSearchParam('company');
  const [eventType] = useSearchParam('eventType');
  const [integrationId] = useSearchParam('integrationId');

  const [state, setState] = useState({
    deleteDialogOpen: false,
    createIntegrationOpen: false,
    values: emptyValues,
    errors: {},
    errorMessage: ''
  });
  const [uiType, setUiType] = useState('');

  const companyOptions = companies.map((companyName) => ({
    value: companyName || 'no-company',
    label: companyName || 'No company'
  }));

  const eventTypeOptions = eventTypes.map((type) => ({
    value: type,
    label: type
  }));

  const hasActiveFilters = Boolean(company || eventType || integrationId);

  const toggleCreateIntegration = () => {
    setState({
      ...state,
      createIntegrationOpen: !state.createIntegrationOpen
    });
  };

  const deleteSelected = async () => {
    try {
      await Promise.all(selectedIntegrations.map(deleteIntegration));
      setIntegrations([
        ...integrations.filter(
          (integration) => indexOf(integration._id, selectedIntegrations) === -1
        )
      ]);
      setSelectedIntegrations([]);
    } catch (err) {
      console.error(err);
    }
  };

  const onSubmitIntegration = async (event) => {
    event.preventDefault();
    if (Object.values(state.errors).some((e) => e === true)) return;

    let submitValues = state.values;
    if (fixedEventTypeFor(uiType)) {
      const seedTalentErrors = validateSeedTalentIntegration(state.values);
      if (Object.keys(seedTalentErrors).length > 0) {
        setState({ ...state, seedTalentErrors });
        return;
      }
      submitValues = trimSeedTalentValues(state.values);
    }

    const values = {
      companyName: submitValues.companyName,
      eventType: submitValues.eventType,
      runnable: !!submitValues.runnable,
      config: JSON.parse(submitValues.config || '{ }'),
      source: JSON.parse(submitValues.source || '{ }')
    };

    try {
      const newIntegration = await createIntegration(values);
      setIntegrations([...integrations, newIntegration]);
      setState({
        deleteDialogOpen: false,
        createIntegrationOpen: false,
        values: emptyValues,
        errors: {},
        errorMessage: '',
        seedTalentErrors: {}
      });
      setUiType('');
    } catch (err) {
      console.log(err);
      setState({
        ...state,
        errorMessage: err.message
      });
    }
  };

  return (
    <div {...rest}>
      <ActionsRow>
        <Spacer />
        <ConfirmButton
          acceptButtonText="Delete"
          dialogBody="Are you sure you want to delete the selected Integrations?"
          dialogTitle="Delete Integrations?"
          disabled={!selectedIntegrations.length}
          onConfirm={deleteSelected}
          openDialogButtonText="Delete"
        />
        <Button
          color="primary"
          onClick={toggleCreateIntegration}
          variant="contained"
        >
          Add Integration
        </Button>
        <ModalContext.Provider
          value={{
            formState: state,
            setFormState: setState,
            onSubmitIntegration,
            isUpdate: false,
            uiType,
            setUiType,
            isOpen: state.createIntegrationOpen,
            onClose: toggleCreateIntegration
          }}
        >
          <IntegrationModal />
        </ModalContext.Provider>
      </ActionsRow>
      <FilterPanel>
        <SearchableSelect
          label="Company"
          onChange={onCompanyChange}
          options={companyOptions}
          placeholder="Search company"
          value={company}
        />
        <SearchableSelect
          label="Type"
          onChange={onEventTypeChange}
          options={eventTypeOptions}
          placeholder="Search type"
          value={eventType}
        />
        <ClearableTextFilter
          label="Integration ID"
          onChange={onIntegrationIdChange}
          placeholder="Filter by integration id"
          value={integrationId}
        />
        <Button
          color="secondary"
          disabled={!hasActiveFilters}
          onClick={onClearAll}
          size="small"
          variant="outlined"
        >
          Clear all
        </Button>
      </FilterPanel>
    </div>
  );
};

IntegrationsToolbar.propTypes = {
  integrations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      companyName: PropTypes.string,
      integrationId: PropTypes.string,
      eventType: PropTypes.string,
      runnable: PropTypes.bool,
      config: PropTypes.object,
      source: PropTypes.object,
      createdDate: PropTypes.string,
      updatedDate: PropTypes.string
    })
  ),
  selectedIntegrations: PropTypes.arrayOf(PropTypes.string),
  setIntegrations: PropTypes.func,
  setSelectedIntegrations: PropTypes.func
};

export default IntegrationsToolbar;
